import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Check, X, Loader2, Trash2, ChevronDown, ChevronRight, Send, Receipt, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../pages/ProjectsList';

const INV_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  void: { label: 'Void', color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300' },
};

const PAY_METHODS = {
  check: 'Check', ach: 'ACH', credit_card: 'Credit Card', cash: 'Cash', other: 'Other',
};

export function InvoicesPayments({ projectId, canEdit }) {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invExpanded, setInvExpanded] = useState(true);
  const [payExpanded, setPayExpanded] = useState(true);
  const [addingInv, setAddingInv] = useState(false);
  const [addingPay, setAddingPay] = useState(false);
  const [invForm, setInvForm] = useState({ invoice_number: '', description: '', amount: '', due_date: '' });
  const [payForm, setPayForm] = useState({ invoice_id: '', amount: '', method: 'check', reference: '', date_received: new Date().toISOString().split('T')[0], notes: '' });
  const [saving, setSaving] = useState(false);
  const [invoicedTotal, setInvoicedTotal] = useState(0);
  const [collectedTotal, setCollectedTotal] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [invRes, payRes] = await Promise.all([
        projectsAPI.listInvoices(projectId),
        projectsAPI.listPayments(projectId),
      ]);
      setInvoices(invRes.data.invoices || []);
      setPayments(payRes.data.payments || []);
      setInvoicedTotal(invRes.data.invoiced_total || 0);
      setCollectedTotal(payRes.data.total || 0);
    } catch {
      toast.error('Failed to load invoices/payments');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Invoice actions
  const handleAddInvoice = async () => {
    if (!invForm.amount) return;
    setSaving(true);
    try {
      await projectsAPI.createInvoice(projectId, {
        invoice_number: invForm.invoice_number || undefined,
        description: invForm.description || undefined,
        amount: parseFloat(invForm.amount),
        due_date: invForm.due_date || undefined,
      });
      setInvForm({ invoice_number: '', description: '', amount: '', due_date: '' });
      setAddingInv(false);
      toast.success('Invoice created');
      fetchData();
    } catch {
      toast.error('Failed to create invoice');
    } finally { setSaving(false); }
  };

  const handleInvStatus = async (invId, newStatus) => {
    try {
      await projectsAPI.updateInvoice(projectId, invId, { status: newStatus });
      toast.success(`Invoice ${newStatus}`);
      fetchData();
    } catch {
      toast.error('Failed to update invoice');
    }
  };

  const handleDeleteInvoice = async (invId) => {
    try {
      await projectsAPI.deleteInvoice(projectId, invId);
      toast.success('Invoice deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete invoice');
    }
  };

  // Payment actions
  const handleAddPayment = async () => {
    if (!payForm.amount || !payForm.date_received) return;
    setSaving(true);
    try {
      await projectsAPI.createPayment(projectId, {
        invoice_id: payForm.invoice_id || undefined,
        amount: parseFloat(payForm.amount),
        method: payForm.method || undefined,
        reference: payForm.reference || undefined,
        date_received: payForm.date_received,
        notes: payForm.notes || undefined,
      });
      setPayForm({ invoice_id: '', amount: '', method: 'check', reference: '', date_received: new Date().toISOString().split('T')[0], notes: '' });
      setAddingPay(false);
      toast.success('Payment recorded');
      fetchData();
    } catch {
      toast.error('Failed to record payment');
    } finally { setSaving(false); }
  };

  const handleDeletePayment = async (payId) => {
    try {
      await projectsAPI.deletePayment(projectId, payId);
      toast.success('Payment deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete payment');
    }
  };

  if (loading) return null;

  const sentInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'paid');

  return (
    <div className="space-y-4" data-testid="invoices-payments-section">
      {/* Invoices */}
      <div className="border rounded-lg overflow-hidden" data-testid="invoices-section">
        <div
          className="flex items-center justify-between p-3 bg-muted/20 cursor-pointer select-none"
          onClick={() => setInvExpanded(p => !p)}
        >
          <div className="flex items-center gap-2">
            {invExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            <Receipt className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Invoices</span>
            <Badge variant="secondary" className="text-[10px]">{invoices.length}</Badge>
            {invoicedTotal > 0 && (
              <span className="text-xs font-semibold text-blue-600 tabular-nums">
                {formatCurrency(invoicedTotal)} invoiced
              </span>
            )}
          </div>
          {canEdit && invExpanded && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={e => { e.stopPropagation(); setAddingInv(true); }} data-testid="add-invoice-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Invoice
            </Button>
          )}
        </div>

        {invExpanded && (
          <div className="divide-y">
            {/* Add invoice form */}
            {addingInv && (
              <div className="flex gap-2 items-end p-3 bg-primary/5 flex-wrap" data-testid="invoice-form" onClick={e => e.stopPropagation()}>
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Invoice #</label>
                  <Input value={invForm.invoice_number} onChange={e => setInvForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="INV-001" className="h-8 text-sm w-[100px]" data-testid="inv-number-input" />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="text-[10px] text-muted-foreground font-medium">Description</label>
                  <Input value={invForm.description} onChange={e => setInvForm(f => ({ ...f, description: e.target.value }))} placeholder="Progress billing #1" className="h-8 text-sm" data-testid="inv-description-input" />
                </div>
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Amount *</label>
                  <Input type="number" value={invForm.amount} onChange={e => setInvForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" className="h-8 text-sm text-right w-[110px]" data-testid="inv-amount-input" />
                </div>
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Due Date</label>
                  <Input type="date" value={invForm.due_date} onChange={e => setInvForm(f => ({ ...f, due_date: e.target.value }))} className="h-8 text-sm w-[130px]" data-testid="inv-due-date-input" />
                </div>
                <Button size="sm" className="h-8" onClick={handleAddInvoice} disabled={saving || !invForm.amount} data-testid="inv-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAddingInv(false); setInvForm({ invoice_number: '', description: '', amount: '', due_date: '' }); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}

            {invoices.length === 0 && !addingInv && (
              <div className="p-4 text-center text-sm text-muted-foreground" data-testid="no-invoices">No invoices yet</div>
            )}

            {invoices.map(inv => {
              const cfg = INV_STATUS[inv.status] || INV_STATUS.draft;
              const hasBalance = inv.balance > 0 && inv.status === 'sent';
              return (
                <div key={inv.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`invoice-${inv.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${inv.status === 'void' ? 'line-through text-muted-foreground' : ''}`}>
                        {inv.invoice_number || `Draft #${inv.id.slice(0, 6)}`}
                      </span>
                      {inv.description && <span className="text-muted-foreground text-xs truncate">— {inv.description}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {inv.creator_name && `by ${inv.creator_name}`}
                      {inv.date_sent && ` · sent ${inv.date_sent}`}
                      {inv.due_date && ` · due ${inv.due_date}`}
                      {inv.paid_amount > 0 && ` · ${formatCurrency(inv.paid_amount)} paid`}
                    </p>
                  </div>
                  <span className="tabular-nums font-semibold">{formatCurrency(inv.amount)}</span>
                  {hasBalance && <span className="text-xs text-amber-600 tabular-nums">bal {formatCurrency(inv.balance)}</span>}
                  <Badge variant="outline" className={`text-[10px] ${cfg.color}`} data-testid={`inv-status-${inv.id}`}>{cfg.label}</Badge>
                  {canEdit && inv.status === 'draft' && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleInvStatus(inv.id, 'sent')} title="Mark Sent" data-testid={`inv-send-${inv.id}`}>
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {canEdit && inv.status === 'sent' && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handleInvStatus(inv.id, 'paid')} title="Mark Paid" data-testid={`inv-paid-${inv.id}`}>
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={() => handleDeleteInvoice(inv.id)} title="Delete" data-testid={`inv-delete-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Payments */}
      <div className="border rounded-lg overflow-hidden" data-testid="payments-section">
        <div
          className="flex items-center justify-between p-3 bg-muted/20 cursor-pointer select-none"
          onClick={() => setPayExpanded(p => !p)}
        >
          <div className="flex items-center gap-2">
            {payExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Payments</span>
            <Badge variant="secondary" className="text-[10px]">{payments.length}</Badge>
            {collectedTotal > 0 && (
              <span className="text-xs font-semibold text-green-600 tabular-nums">
                {formatCurrency(collectedTotal)} collected
              </span>
            )}
          </div>
          {canEdit && payExpanded && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={e => { e.stopPropagation(); setAddingPay(true); }} data-testid="add-payment-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Record Payment
            </Button>
          )}
        </div>

        {payExpanded && (
          <div className="divide-y">
            {/* Add payment form */}
            {addingPay && (
              <div className="flex gap-2 items-end p-3 bg-primary/5 flex-wrap" data-testid="payment-form" onClick={e => e.stopPropagation()}>
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Amount *</label>
                  <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" className="h-8 text-sm text-right w-[110px]" data-testid="pay-amount-input" />
                </div>
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Date *</label>
                  <Input type="date" value={payForm.date_received} onChange={e => setPayForm(f => ({ ...f, date_received: e.target.value }))} className="h-8 text-sm w-[130px]" data-testid="pay-date-input" />
                </div>
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Method</label>
                  <Select value={payForm.method} onValueChange={v => setPayForm(f => ({ ...f, method: v }))}>
                    <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PAY_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {sentInvoices.length > 0 && (
                  <div className="flex-shrink-0">
                    <label className="text-[10px] text-muted-foreground font-medium">Against Invoice</label>
                    <Select value={payForm.invoice_id || 'none'} onValueChange={v => setPayForm(f => ({ ...f, invoice_id: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="pay-invoice-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No invoice</SelectItem>
                        {sentInvoices.map(i => <SelectItem key={i.id} value={i.id}>{i.invoice_number || i.id.slice(0, 8)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex-shrink-0">
                  <label className="text-[10px] text-muted-foreground font-medium">Reference</label>
                  <Input value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="Check #, Txn ID" className="h-8 text-sm w-[120px]" data-testid="pay-reference-input" />
                </div>
                <Button size="sm" className="h-8" onClick={handleAddPayment} disabled={saving || !payForm.amount || !payForm.date_received} data-testid="pay-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record'}
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAddingPay(false); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}

            {payments.length === 0 && !addingPay && (
              <div className="p-4 text-center text-sm text-muted-foreground" data-testid="no-payments">No payments recorded yet</div>
            )}

            {payments.map(pay => (
              <div key={pay.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`payment-${pay.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatCurrency(pay.amount)}</span>
                    {pay.method && <Badge variant="outline" className="text-[10px]">{PAY_METHODS[pay.method] || pay.method}</Badge>}
                    {pay.invoice_number && <span className="text-xs text-muted-foreground">for {pay.invoice_number}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {pay.date_received}
                    {pay.reference && ` · Ref: ${pay.reference}`}
                    {pay.creator_name && ` · by ${pay.creator_name}`}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={() => handleDeletePayment(pay.id)} title="Delete" data-testid={`pay-delete-${pay.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
